import { useState, useCallback } from 'react';

interface DiagnosticResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  report: string;
  recommendations: string[];
}

export const useSignatureDiagnostic = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [lastDiagnostic, setLastDiagnostic] = useState<DiagnosticResult | null>(null);

  const diagnoseSignature = useCallback(async (
    signature: string,
    walletAddress: string,
    message: string
  ): Promise<DiagnosticResult> => {
    setIsLoading(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      
      const response = await fetch(`${BACKEND_URL}/api/auth/debug-signature`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          signature,
          walletAddress,
          message
        })
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      const result: DiagnosticResult = {
        isValid: data.diagnostic?.isValid || false,
        errors: data.diagnostic?.errors || [],
        warnings: data.diagnostic?.warnings || [],
        report: data.report || 'Aucun rapport disponible',
        recommendations: data.recommendations || []
      };

      setLastDiagnostic(result);
      return result;
      
    } catch (error) {
      const errorResult: DiagnosticResult = {
        isValid: false,
        errors: [`Erreur de diagnostic: ${error instanceof Error ? error.message : 'Erreur inconnue'}`],
        warnings: [],
        report: 'Impossible de générer le rapport',
        recommendations: [
          'Vérifiez que le backend est accessible',
          'Vérifiez votre connexion internet',
          'Contactez le support technique'
        ]
      };
      
      setLastDiagnostic(errorResult);
      return errorResult;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearDiagnostic = useCallback(() => {
    setLastDiagnostic(null);
  }, []);

  return {
    diagnoseSignature,
    clearDiagnostic,
    isLoading,
    lastDiagnostic
  };
};
